import { useEffect, useRef, useState } from "react";
import * as Ads from "../../Utility/Ads/Ads";
import styles from "./ReleasingLeaderBoard.custom.module.css";
import cx from "classnames";

// Collapse once the user scrolls past this point...
const HIDE_AT = 800;
// ...and only expand again when they return near the very top. This gap must be
// larger than the leaderboard's collapsed height, otherwise the layout shift from
// collapsing drops scrollY back into the "show" band and the ad flickers.
const SHOW_AT = 0;

// GAM's Active View only counts an impression as "viewed" once at least
// half the ad is on screen, continuously, for 1 second (support.google.com/
// admanager/answer/6199883). VIEWABLE_DWELL_MS adds a 500ms margin above
// that 1s minimum against timing imprecision (frame drops, observer delays).
const VIEWABLE_RATIO = 0.5;
const VIEWABLE_DWELL_MS = 1500;

/**
 * Dispatched (bubbling) by the SDK ad bridge on the slot's container after
 * every GPT render. Keep the name in sync with
 * `src/app/utils/sdkAds/SdkAdPlacements.tsx`'s `SLOT_RENDER_ENDED_EVENT`.
 */
const SLOT_RENDER_ENDED_EVENT = "sdkAdSlotRenderEnded";

export default function ReleasingLeaderboardV2() {
  const [isCollapsed, setIsCollapsed] = useState(false);
  // Sticky offset = the live height of the fixed <header>. Hardcoding it (e.g.
  // 119px for desktop) leaves a white gap on mobile, where the header is
  // shorter than the desktop value, so the ad sticks below the header bottom.
  const [headerHeight, setHeaderHeight] = useState<number>();
  // Bumped on every real (non-empty) GPT render of this slot so the
  // viewability watcher below re-arms per impression, instead of once for
  // the component's whole lifetime — a refreshed/replaced creative (e.g. a
  // fresh ad on SPA article-to-article navigation, with this wrapper never
  // unmounting) must earn its own viewable moment, not coast on an earlier,
  // unrelated impression's credit.
  const [renderGeneration, setRenderGeneration] = useState(0);
  // Ref mirror of the state so the scroll handler can read the latched value
  // without re-subscribing the listener on every toggle.
  const collapsedRef = useRef(false);
  // True once the current creative has been genuinely viewable (see the
  // IntersectionObserver effect below) — collapse must never happen first.
  const hasBeenViewableRef = useRef(false);
  const slotRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const header = document.querySelector("header");
    if (!header) return;

    const measure = () =>
      setHeaderHeight(header.getBoundingClientRect().height);

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(header);

    return () => observer.disconnect();
  }, []);

  // Re-arm the viewability watcher for every new GPT render of this slot (see
  // the effect below): reset the latch and bump renderGeneration so the
  // IntersectionObserver effect re-subscribes fresh for the new creative,
  // and force the wrapper back open so the new creative actually has a
  // chance to be seen instead of rendering straight into an already
  // collapsed (0-height) box.
  useEffect(() => {
    const el = slotRef.current;
    if (!el) return;

    const onSlotRenderEnded = (event: Event) => {
      const isEmpty =
        (event as CustomEvent<{ isEmpty: boolean }>).detail?.isEmpty ?? false;
      if (isEmpty) return;

      hasBeenViewableRef.current = false;
      collapsedRef.current = false;
      setIsCollapsed(false);
      setRenderGeneration((generation) => generation + 1);
    };

    el.addEventListener(SLOT_RENDER_ENDED_EVENT, onSlotRenderEnded);
    return () =>
      el.removeEventListener(SLOT_RENDER_ENDED_EVENT, onSlotRenderEnded);
  }, []);

  // Gate the collapse behaviour on the current creative having actually been
  // viewable (>=VIEWABLE_RATIO on screen for a full continuous
  // VIEWABLE_DWELL_MS) at least once. Until then, scroll events must not
  // collapse it — the ad stays visible (it is sticky, so on a scrolled-in
  // load it pins to the top of the viewport) long enough for GPT to register
  // a real Active View impression, not just a single-frame flash past it.
  useEffect(() => {
    const el = slotRef.current;
    if (!el) return;

    let dwellTimer: ReturnType<typeof setTimeout> | undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries.at(-1);
        if (!entry) return;

        if (entry.intersectionRatio >= VIEWABLE_RATIO) {
          dwellTimer ??= setTimeout(() => {
            hasBeenViewableRef.current = true;
            observer.disconnect();
            // Nudge the scroll evaluator to react immediately instead of
            // waiting for the next real scroll event.
            window.dispatchEvent(new Event("scroll"));
          }, VIEWABLE_DWELL_MS);
        } else if (dwellTimer !== undefined) {
          clearTimeout(dwellTimer);
          dwellTimer = undefined;
        }
      },
      { threshold: [VIEWABLE_RATIO] },
    );
    observer.observe(el);

    return () => {
      observer.disconnect();
      if (dwellTimer !== undefined) clearTimeout(dwellTimer);
    };
  }, [renderGeneration]);

  useEffect(() => {
    let ticking = false;

    const evaluate = () => {
      ticking = false;
      const y = window.scrollY;

      // Never collapse before the current creative has actually been viewable.
      if (!hasBeenViewableRef.current) return;

      if (!collapsedRef.current && y > HIDE_AT) {
        collapsedRef.current = true;
        setIsCollapsed(true);
      } else if (collapsedRef.current && y <= SHOW_AT) {
        collapsedRef.current = false;
        setIsCollapsed(false);
      }
    };

    const handleScroll = () => {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(evaluate);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    evaluate();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <div
      className={cx(styles.wrapper, isCollapsed && styles.hide)}
      style={headerHeight ? { top: `${headerHeight}px` } : undefined}
    >
      <div ref={slotRef} className={styles.innerWrapper}>
        <Ads.AdsLeaderboard />
      </div>
    </div>
  );
}
